// Map view — Leaflet + OSM tiles.
// Preferred-set rules:
//   - Initial "Using location" activation: UNION circle into existing selections
//   - Drag center / slider: REPLACE with stores in circle
//   - Click pin: toggle individual store
//   - Clear all: wipe everything

import React, { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";
import leafUrl from "./leaf.svg";
import type { GeoCircle } from "./types";

// Leaflet's default marker icon URLs are derived from its own script URL,
// which doesn't exist when bundled — bake in the file-loader URLs so the
// markers render after Webpack/esbuild output paths.
delete (L.Icon.Default.prototype as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

const cssVar = (name: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

function haversine_m(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1),
    dLon = toRad(lon2 - lon1);
  return (
    2 *
    R *
    Math.asin(
      Math.sqrt(
        Math.sin(dLat / 2) ** 2 +
          Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2,
      ),
    )
  );
}

// dispensary_name and dispensary_url originate from scraped third-party
// platforms.  The tooltip is built as an HTML string for Leaflet's
// bindTooltip(), so untrusted text must be escaped and untrusted URLs must be
// restricted to http(s) before they reach innerHTML — otherwise a hostile
// store name (<img onerror>) or a javascript: URL would run in the visitor's
// browser.  The rest of the app renders these through React and is already
// safe; this is the one raw-HTML sink.
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;",
  );
}

// Return the URL only if it parses as http(s); otherwise null so callers can
// drop the link entirely.
function safeHttpUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url, window.location.origin);
    return u.protocol === "http:" || u.protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

function storesInCircle(lat: number, lng: number, radius_m: number): Set<string> {
  const inside = new Set<string>();
  window.WEEDBUD_DATA!.dispensaries.forEach((d) => {
    if (haversine_m(lat, lng, d.latitude, d.longitude) <= radius_m)
      inside.add(d.dispensary_id + "|" + d.platform);
  });
  return inside;
}

// What releasing the center-handle drag now would change: stores to add,
// stores to drop, and how many the moved circle holds (null when no drag is
// under way).
export interface PendingCounts {
  add: number;
  remove: number;
  inside: number | null;
}
export const NO_PENDING: PendingCounts = { add: 0, remove: 0, inside: null };

// CARTO stamps "API KEY REQUIRED" across every tile served without a key,
// so an unset key is visible to the visitor but still draws a usable map.
// The key is public by nature -- it ships in the data blob and travels in
// the tile URL; CARTO scopes it by referring domain, not by secrecy.
const TILE_URL = "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";

function tileUrl(key: string | null | undefined): string {
  return key ? `${TILE_URL}?key=${encodeURIComponent(key)}` : TILE_URL;
}

// Required by both upstreams: CARTO's terms (carto.com/attributions) and
// the ODbL for the OpenStreetMap data the tiles are rendered from.  Must
// stay visible on every viewport, mobile included.
const TILE_ATTRIBUTION =
  '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">' +
  "&copy; OpenStreetMap</a> contributors &middot; " +
  '<a href="https://carto.com/attributions" target="_blank" rel="noopener noreferrer">' +
  "&copy; CARTO</a>";

// PA proper is [[39.5,-80.8],[42.5,-74.3]]; the ~0.3° (~20mi) buffer lets
// the user drag into adjacent states but stays bounded.
const EDGE_BOUNDS = L.latLngBounds([
  [39.2, -81.1],
  [42.8, -74.0],
]);

const DEFAULT_CENTER: L.LatLngTuple = [40.85, -77.5];
const DEFAULT_ZOOM = 7;
const MAP_VIEW_KEY = "wb.mapview.v1";

// The user's last pan/zoom, restored on the next load. Center/zoom live only
// inside the Leaflet instance otherwise (unlike geoCircle/preferred, which
// are lifted to app.tsx state) -- read/write happens directly against
// localStorage from this module, same pattern as wb.rail.v1 in table.tsx.
function loadMapView(): { center: L.LatLngTuple; zoom: number } | null {
  try {
    const raw = JSON.parse(localStorage.getItem(MAP_VIEW_KEY) || "null");
    if (
      typeof raw?.lat !== "number" ||
      typeof raw?.lng !== "number" ||
      typeof raw?.zoom !== "number"
    )
      return null;
    // Clamp rather than discard: a stored point just outside EDGE_BOUNDS
    // (e.g. the edge buffer shrinks in a future change) should still load
    // near where the user left off, not silently fall back to the default.
    const lat = Math.max(EDGE_BOUNDS.getSouth(), Math.min(EDGE_BOUNDS.getNorth(), raw.lat));
    const lng = Math.max(EDGE_BOUNDS.getWest(), Math.min(EDGE_BOUNDS.getEast(), raw.lng));
    const zoom = Math.max(1, Math.min(19, raw.zoom));
    return { center: [lat, lng], zoom };
  } catch {
    return null;
  }
}

function saveMapView(m: L.Map): void {
  try {
    const c = m.getCenter();
    localStorage.setItem(
      MAP_VIEW_KEY,
      JSON.stringify({ lat: c.lat, lng: c.lng, zoom: m.getZoom() }),
    );
  } catch {
    /* private mode / quota exceeded -- view just won't persist */
  }
}

export interface MapViewProps {
  preferred: Set<string>;
  setPreferred: React.Dispatch<React.SetStateAction<Set<string>>>;
  geoCircle: GeoCircle | null;
  setGeoCircle: React.Dispatch<React.SetStateAction<GeoCircle | null>>;
  onPendingChange: (counts: PendingCounts) => void;
  onMapReady?: (m: L.Map) => void;
}

export function MapView({
  preferred,
  setPreferred,
  geoCircle,
  setGeoCircle,
  onPendingChange,
  onMapReady,
}: MapViewProps) {
  const mapEl = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Record<string, L.Marker>>({});
  const circleRef = useRef<L.Circle | null>(null);
  const centerRef = useRef<L.Marker | null>(null);
  const preferredRef = useRef<Set<string>>(preferred); // always-current ref for event handlers
  const [ready, setReady] = useState(false);
  const [accentTick, setAccentTick] = useState(0);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);
  const preDragRef = useRef<{ lat: number; lng: number } | null>(null); // circle center before drag starts — restored on Escape
  const dragCancelledRef = useRef(false); // Escape sets this; dragend checks it before committing
  const onPendingChangeRef = useRef(onPendingChange); // stable ref so Escape handler isn't stale
  const prevPreferredRef = useRef<Set<string> | null>(null); // tracks last-rendered preferred set for setIcon diffing
  const lastAccentRef = useRef<string | null>(null); // last accent applied to the geo circle
  const selfUpdateRef = useRef(false); // set by drag handlers before setGeoCircle to skip redundant Leaflet mutations
  const mapDraggingRef = useRef(false); // true while the user is panning the map
  const geoCircleRef = useRef<GeoCircle | null>(geoCircle); // always-current geoCircle for tooltip distance
  const onMapReadyRef = useRef(onMapReady);
  useEffect(() => {
    onMapReadyRef.current = onMapReady;
  }, [onMapReady]);

  // Keep refs in sync with props/state
  useEffect(() => {
    preferredRef.current = preferred;
  }, [preferred]);
  useEffect(() => {
    onPendingChangeRef.current = onPendingChange;
  }, [onPendingChange]);
  useEffect(() => {
    geoCircleRef.current = geoCircle;
  }, [geoCircle]);

  // ── mount map ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const savedView = loadMapView();
    const m = L.map(mapEl.current, {
      center: savedView?.center ?? DEFAULT_CENTER,
      zoom: savedView?.zoom ?? DEFAULT_ZOOM,
      zoomControl: false,
      attributionControl: false,
      preferCanvas: true,
      maxBounds: EDGE_BOUNDS,
      maxBoundsViscosity: 1.0,
      inertia: false,
    });
    L.control.zoom({ position: "bottomright" }).addTo(m);
    // prefix: false drops Leaflet's own "Leaflet" credit, which neither
    // upstream requires, so only the two mandatory credits show.
    L.control.attribution({ position: "bottomleft", prefix: false }).addTo(m);
    L.tileLayer(tileUrl(window.WEEDBUD_DATA?.carto_api_key), {
      maxZoom: 19,
      attribution: TILE_ATTRIBUTION,
    }).addTo(m);
    mapRef.current = m;
    // Attach to the container element for test access (not a window global).
    (mapEl.current as HTMLDivElement & { _leafletMap: L.Map })._leafletMap = m;
    onMapReadyRef.current?.(m);
    setReady(true);
    const fitMinZoom = () => {
      const size = m.getSize();
      // The map mounts while the tab wrapper is display:none (query
      // is the default tab) -- size 0x0 makes getBoundsZoom return a
      // garbage low number, which would let the user zoom to planet
      // level.  Bail until the container actually has dimensions; the
      // 'resize' event below will retry once it does.
      if (size.x === 0 || size.y === 0) return;
      // inside=false (default): largest zoom at which the bounds still
      // fit entirely in the view.  inside=true returns the OPPOSITE
      // (smallest zoom at which the view fits in the bounds), which is
      // why earlier attempts still let the user zoom to planet level.
      const z = m.getBoundsZoom(EDGE_BOUNDS);
      m.setMinZoom(z);
      if (m.getZoom() < z) m.setZoom(z);
    };
    // Leaflet fires 'resize' from invalidateSize(); the app.tsx
    // switchTab handler calls invalidateSize on every map-tab visit, so
    // this picks up the first paint with real dimensions and every
    // viewport change after.
    m.on("resize", fitMinZoom);
    const onMoveEnd = () => saveMapView(m);
    m.on("moveend", onMoveEnd);
    m.on("dragstart", () => {
      mapDraggingRef.current = true;
    });
    m.on("dragend", () => {
      mapDraggingRef.current = false;
      // Close any tooltips that opened as the mouse passed over markers during the drag.
      Object.values(markersRef.current).forEach((mk) => mk.closeTooltip());
    });
    // Also try right after mount in case the map is already visible.
    setTimeout(fitMinZoom, 50);
    return () => {
      m.off("resize", fitMinZoom);
      m.off("moveend", onMoveEnd);
    };
  }, []);

  // ── store markers ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!ready) return;
    const m = mapRef.current!;
    const prev = prevPreferredRef.current;
    window.WEEDBUD_DATA!.dispensaries.forEach((d) => {
      const key = d.dispensary_id + "|" + d.platform;
      const isPreferred = preferred.has(key);
      const html = `<div class="wb-marker ${isPreferred ? "is-on" : ""}"><img class="wb-leaf" src="${leafUrl}" alt="" aria-hidden="true"/></div>`;
      const icon = L.divIcon({
        className: "wb-marker-wrap",
        html,
        iconSize: [26, 26],
        iconAnchor: [13, 13],
      });
      const existing = markersRef.current[key];
      if (existing) {
        // Only rebuild the DOM element when is-on membership actually changed.
        if (prev === null || isPreferred !== prev.has(key)) existing.setIcon(icon);
        return;
      }
      const mk = L.marker([d.latitude, d.longitude], { icon }).addTo(m);
      const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${d.latitude},${d.longitude}`;
      const nameHtml = escapeHtml(d.dispensary_name ?? "");
      const shopUrl = safeHttpUrl(d.dispensary_url);
      const buildTipHtml = () => {
        const gc = geoCircleRef.current;
        const distMi = gc
          ? Math.round(haversine_m(gc.lat, gc.lng, d.latitude, d.longitude) / 1609.34)
          : null;
        const distSpan = distMi !== null ? `<span class="wb-popup-dist">${distMi} mi</span>` : "";
        const shopHtml = shopUrl
          ? `<a class="wb-popup-shop" href="${escapeHtml(shopUrl)}" target="_blank" rel="noopener">Shop ↗</a>`
          : "";
        return `<div class="wb-tip"><div class="wb-tip-name">${nameHtml}</div><div class="wb-tip-footer">${distSpan}<a class="wb-popup-dir" href="${mapsUrl}" target="_blank" rel="noopener">Directions ↗</a>${shopHtml}</div></div>`;
      };
      mk.bindTooltip(buildTipHtml(), {
        direction: "top",
        offset: [0, -8],
        className: "wb-tooltip",
        interactive: true,
      });
      // Replace Leaflet's built-in mouseover/mouseout open/close with manual management:
      //   • Suppress opens while the map is being dragged (fixes ghost tooltips on drag).
      //   • Delay close by 250 ms so the mouse can cross from marker to tooltip without
      //     the tooltip disappearing mid-travel (fixes the Directions link being unreachable).
      //   • Recompute distance from the current geoCircle before opening.
      mk.off("mouseover");
      mk.off("mouseout");
      let tipTimer: ReturnType<typeof setTimeout> | null = null;
      const openTip = () => {
        if (mapDraggingRef.current) return;
        if (tipTimer) clearTimeout(tipTimer);
        // Hover events from inside an open tooltip also reach this handler.
        // Its content is left alone, so a link being tapped stays in place.
        if (mk.isTooltipOpen()) return;
        mk.getTooltip()?.setContent(buildTipHtml());
        mk.openTooltip();
      };
      const closeTip = () => {
        if (tipTimer) clearTimeout(tipTimer);
        tipTimer = setTimeout(() => mk.closeTooltip(), 250);
      };
      mk.on("mouseover", openTip);
      mk.on("mouseout", closeTip);
      // Once the tooltip DOM element exists, keep it open while the mouse is on it.
      mk.once("tooltipopen", (ev: L.LeafletEvent) => {
        const tt = (ev as L.LeafletEvent & { tooltip?: L.Tooltip }).tooltip;
        const el = tt?.getElement?.();
        if (!el) return;
        el.addEventListener("mouseenter", () => {
          if (tipTimer) clearTimeout(tipTimer);
        });
        el.addEventListener("mouseleave", closeTip);
        // A press inside the tooltip leaves focus on the marker, since Leaflet
        // closes the tooltip when the marker loses focus. Without this, the
        // press would focus the link, and Leaflet focuses the map container
        // on any press inside the map. A link's click still fires.
        el.addEventListener("mousedown", (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
      });
      mk.on("click", () => {
        setPreferred((prev) => {
          const n = new Set(prev);
          if (n.has(key)) n.delete(key);
          else n.add(key);
          return n;
        });
      });
      markersRef.current[key] = mk;
    });
    prevPreferredRef.current = preferred;
  }, [ready, preferred, accentTick, setPreferred]);

  // accent changes → re-render markers (only when --accent actually changes)
  useEffect(() => {
    let last = cssVar("--accent");
    const obs = new MutationObserver(() => {
      const now = cssVar("--accent");
      if (now !== last) {
        last = now;
        setAccentTick((t) => t + 1);
      }
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["style"] });
    return () => obs.disconnect();
  }, []);

  // ── helpers for drag visual feedback ───────────────────────────────────
  // Compute and visualize the pending state during a drag:
  //   is-pending-remove: currently selected, will be dropped
  //   is-pending-add:    not currently selected, will be added
  // Markers in neither class show their committed state from React.
  const updatePendingRemove = useCallback(
    (circleLat: number, circleLng: number, circleR: number) => {
      const current = preferredRef.current;
      let nRemove = 0;
      let nAdd = 0;
      let nInside = 0;
      window.WEEDBUD_DATA!.dispensaries.forEach((d) => {
        const key = d.dispensary_id + "|" + d.platform;
        const willBeInside = haversine_m(circleLat, circleLng, d.latitude, d.longitude) <= circleR;
        if (willBeInside) nInside++;
        const mk = markersRef.current[key];
        if (!mk) return;
        const el = mk.getElement();
        if (!el) return;
        const isCurrentlySelected = current.has(key);
        const isPendingRemove = isCurrentlySelected && !willBeInside;
        const isPendingAdd = !isCurrentlySelected && willBeInside;
        el.classList.toggle("is-pending-remove", isPendingRemove);
        el.classList.toggle("is-pending-add", isPendingAdd);
        if (isPendingRemove) nRemove++;
        if (isPendingAdd) nAdd++;
      });
      if (onPendingChangeRef.current)
        onPendingChangeRef.current({ add: nAdd, remove: nRemove, inside: nInside });
    },
    [],
  );

  const clearPendingRemove = useCallback(() => {
    Object.values(markersRef.current).forEach((mk) => {
      const el = mk.getElement();
      if (el) {
        el.classList.remove("is-pending-remove");
        el.classList.remove("is-pending-add");
      }
      mk.closeTooltip();
    });
    if (onPendingChangeRef.current) onPendingChangeRef.current(NO_PENDING);
  }, []);

  // Undo a center-handle drag: move the circle back to where the drag began
  // and drop everything the drag showed. Escape and the cancelled dragend
  // both call it, so whatever a drag displays has one place to be undone.
  const revertDrag = useCallback(() => {
    const pre = preDragRef.current;
    if (!pre) return;
    circleRef.current?.setLatLng([pre.lat, pre.lng]);
    centerRef.current?.setLatLng([pre.lat, pre.lng]);
    clearPendingRemove();
  }, [clearPendingRemove]);

  // ── Escape: cancel an in-progress drag ─────────────────────────────────
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !preDragRef.current) return;
      dragCancelledRef.current = true;
      revertDrag();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [revertDrag]);

  // ── geo circle ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!ready) return;
    const m = mapRef.current!;

    if (!geoCircle) {
      if (circleRef.current) {
        m.removeLayer(circleRef.current);
        circleRef.current = null;
      }
      if (centerRef.current) {
        m.removeLayer(centerRef.current);
        centerRef.current = null;
      }
      return;
    }

    const accent = cssVar("--accent") || "#5CA904";

    if (!circleRef.current) {
      // Create circle
      circleRef.current = L.circle([geoCircle.lat, geoCircle.lng], {
        radius: geoCircle.radius_m,
        color: accent,
        weight: 2,
        fillColor: accent,
        fillOpacity: 0.08,
        dashArray: "5 5",
      }).addTo(m);
      lastAccentRef.current = accent;

      // Center handle — 44px touch target
      const cIcon = L.divIcon({
        className: "",
        html: `<div class="wb-geo-center-inner"></div>`,
        iconSize: [44, 44],
        iconAnchor: [22, 22],
      });
      centerRef.current = L.marker([geoCircle.lat, geoCircle.lng], {
        icon: cIcon,
        draggable: true,
        zIndexOffset: 200,
        autoPan: true,
        autoPanPadding: [60, 60],
        autoPanSpeed: 12,
      }).addTo(m);

      centerRef.current.on("dragstart", () => {
        const ll = circleRef.current!.getLatLng();
        preDragRef.current = { lat: ll.lat, lng: ll.lng };
        dragCancelledRef.current = false;
      });
      centerRef.current.on("drag", () => {
        // Leaflet keeps reporting moves until the mouse is released; after
        // Escape, hold the handle where the drag began.
        if (dragCancelledRef.current) {
          const pre = preDragRef.current!;
          centerRef.current!.setLatLng([pre.lat, pre.lng]);
          return;
        }
        const raw = centerRef.current!.getLatLng();
        const lat = Math.max(EDGE_BOUNDS.getSouth(), Math.min(EDGE_BOUNDS.getNorth(), raw.lat));
        const lng = Math.max(EDGE_BOUNDS.getWest(), Math.min(EDGE_BOUNDS.getEast(), raw.lng));
        const ll = L.latLng(lat, lng);
        centerRef.current!.setLatLng(ll);
        circleRef.current!.setLatLng(ll);
        const r = circleRef.current!.getRadius();
        updatePendingRemove(ll.lat, ll.lng, r);
      });
      centerRef.current.on("dragend", () => {
        if (dragCancelledRef.current) {
          revertDrag();
          preDragRef.current = null;
          dragCancelledRef.current = false;
          return;
        }
        clearPendingRemove();
        const ll = centerRef.current!.getLatLng(); // already clamped by drag handler
        const r = circleRef.current!.getRadius();
        setPreferred(storesInCircle(ll.lat, ll.lng, r));
        selfUpdateRef.current = true;
        setGeoCircle((prev) => (prev ? { ...prev, lat: ll.lat, lng: ll.lng, radius_m: r } : prev));
        preDragRef.current = null;
      });
    } else {
      // Update existing (e.g. slider). Drag handlers already called setLatLng/setRadius
      // during the drag, so skip those mutations when selfUpdateRef is set (dragend path).
      if (!selfUpdateRef.current) {
        circleRef.current.setLatLng([geoCircle.lat, geoCircle.lng]);
        circleRef.current.setRadius(geoCircle.radius_m);
        centerRef.current!.setLatLng([geoCircle.lat, geoCircle.lng]);
      }
      selfUpdateRef.current = false;
      // Only call setStyle when accent actually changed — each call triggers a full canvas redraw.
      if (accent !== lastAccentRef.current) {
        circleRef.current.setStyle({ color: accent, fillColor: accent });
        lastAccentRef.current = accent;
      }
    }
  }, [
    ready,
    geoCircle,
    accentTick,
    clearPendingRemove,
    revertDrag,
    setGeoCircle,
    setPreferred,
    updatePendingRemove,
  ]);

  // ── controls ────────────────────────────────────────────────────────────
  // Only reachable while a geo circle is active (Locate-when-on), so it always
  // refits to the circle's bounds.
  const recenterView = () => {
    const mc = mapRef.current;
    if (!mc || !circleRef.current) return;
    // Internal Leaflet method used to enforce maxBounds; we suspend it during
    // animation so the pane doesn't bounce.  Not in @types/leaflet.
    const mcAny = mc as unknown as { _panInsideMaxBounds: () => void };
    mc.off("moveend", mcAny._panInsideMaxBounds, mc);
    // Hide the overlay pane (canvas) for the duration of the animation.
    // Leaflet's canvas renderer CSS-scales the canvas bitmap on zoomanim,
    // making the 2px stroke appear heavy mid-animation (the "flash").
    const overlayPane = mc.getPane("overlayPane");
    if (overlayPane) overlayPane.style.visibility = "hidden";
    // Restore after the animation duration.  We don't use once("moveend") here:
    // fitBounds() fires moveend SYNCHRONOUSLY from _stop()/_resetView() before
    // the actual zoom animation begins, which would restore the pane prematurely
    // and reintroduce the flash.  Duration-matched timeout is reliable.
    setTimeout(() => {
      if (overlayPane) overlayPane.style.visibility = "";
      mc.on("moveend", mcAny._panInsideMaxBounds, mc);
    }, 450);
    mc.fitBounds(circleRef.current.getBounds(), {
      animate: true,
      duration: 0.4,
      padding: [40, 40],
      maxZoom: 14,
    });
  };

  const useMyLocation = () => {
    if (geoCircle) {
      recenterView(); // already active → recenter on the current circle
      return;
    }
    if (locating) return;
    setLocateError(null);

    const applyCircle = (lat: number, lng: number) => {
      const radius_m = 32187;
      setPreferred((prev) => {
        const n = new Set(prev);
        storesInCircle(lat, lng, radius_m).forEach((k) => n.add(k));
        return n;
      });
      setGeoCircle({ lat, lng, radius_m });
      // Lift maxBounds enforcement for the animation then zoom to fit the
      // new circle.  No overlayPane hide here — the circle doesn't exist in
      // the canvas yet on first locate so there's no bitmap to flash.
      if (mapRef.current) {
        const mc = mapRef.current;
        const mcAny = mc as unknown as { _panInsideMaxBounds: () => void };
        mc.off("moveend", mcAny._panInsideMaxBounds, mc);
        setTimeout(() => mc.on("moveend", mcAny._panInsideMaxBounds, mc), 450);
        const latDelta = radius_m / 111320;
        const lngDelta = radius_m / (111320 * Math.cos((lat * Math.PI) / 180));
        mc.fitBounds(
          [
            [lat - latDelta, lng - lngDelta],
            [lat + latDelta, lng + lngDelta],
          ],
          { animate: true, duration: 0.4, padding: [40, 40], maxZoom: 14 },
        );
      }
    };

    if (!navigator.geolocation) {
      setLocateError("Geolocation not available");
      setTimeout(() => setLocateError(null), 3000);
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        applyCircle(pos.coords.latitude, pos.coords.longitude);
      },
      (err) => {
        setLocating(false);
        const msg =
          err.code === 1
            ? "Location access denied"
            : err.code === 2
              ? "Location unavailable"
              : "Location timed out";
        setLocateError(msg);
        setTimeout(() => setLocateError(null), 3000);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  const clearCircle = () => setGeoCircle(null);

  return (
    <div className="map-pane">
      <div className="map-canvas" ref={mapEl} />
      {locateError && <div className="locate-error">{locateError}</div>}
      <div className="map-callout">
        <div className="map-callout-row">
          <button
            className={
              "map-callout-locate" + (geoCircle ? " is-on" : "") + (locating ? " is-locating" : "")
            }
            onClick={useMyLocation}
            disabled={locating}
            aria-label={locating ? "Locating…" : geoCircle ? "Re-center map" : "Locate me"}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <rect x="3" y="3" width="14" height="14" rx="2" />
              <circle cx="10" cy="10" r="3" />
            </svg>
            <span className="map-callout-locate-label">
              {"Locate within "}
              <span>{((geoCircle?.radius_m ?? 32187) / 1609.34).toFixed(1)} mi</span>
            </span>
          </button>
          <input
            type="range"
            min="2000"
            max="161000"
            step="1000"
            value={geoCircle?.radius_m ?? 32187}
            disabled={!geoCircle}
            onChange={(e) => {
              if (!geoCircle) return;
              const r = +e.target.value;
              setPreferred(storesInCircle(geoCircle.lat, geoCircle.lng, r));
              setGeoCircle((prev) => (prev ? { ...prev, radius_m: r } : prev));
            }}
          />
          {geoCircle && (
            <button className="map-callout-close" onClick={clearCircle} title="Remove radius">
              ×
            </button>
          )}
        </div>
        <div className="map-callout-hint">tap to select or deselect stores</div>
      </div>
    </div>
  );
}
